import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

const instructions = [
  {
    title: "Selecciona el micrositio",
    description:
      "Desde Inicio, entra al área que atenderá tu solicitud, por ejemplo TI, Talento Humano o Administración.",
    icon: "ri-grid-line",
  },
  {
    title: "Elige el servicio",
    description:
      "Ubica la categoría y el servicio que mejor describan lo que necesitas.",
    icon: "ri-list-check-3",
  },
  {
    title: "Abre la solicitud",
    description:
      'Haz clic en “Solicitar Servicio” o en la opción disponible dentro de la tarjeta del servicio.',
    icon: "ri-file-add-line",
  },
  {
    title: "Completa la información",
    description:
      "Verifica tus datos, escribe un asunto claro y explica el requerimiento con el mayor detalle posible.",
    icon: "ri-edit-box-line",
  },
  {
    title: "Envía y conserva el folio",
    description:
      "Envía la solicitud y guarda el número de ticket que confirma que fue registrada.",
    icon: "ri-send-plane-line",
  },
  {
    title: "Consulta el seguimiento",
    description:
      'Entra a “Mis tickets” para revisar el estado, leer respuestas y agregar comentarios.',
    icon: "ri-search-eye-line",
  },
];

export default function TicketInstructionsHelp() {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    const triggerElement = triggerRef.current;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      triggerElement?.focus();
    };
  }, [isOpen]);

  const closeModal = () => setIsOpen(false);

  return (
    <>
      <div className="fixed bottom-5 left-4 md:bottom-7 md:left-7 z-[80] group">
        <div
          id="ticket-help-tooltip"
          role="tooltip"
          className="pointer-events-none absolute bottom-full left-0 mb-3 w-max max-w-[calc(100vw-2rem)] rounded-lg bg-foreground-950 px-3 py-2 text-xs font-medium text-white shadow-lg opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0"
        >
          ¿Cómo levantar un ticket?
          <span className="absolute -bottom-1 left-5 h-2 w-2 rotate-45 bg-foreground-950" />
        </div>

        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label="Ver instrucciones para levantar un ticket"
          aria-describedby="ticket-help-tooltip"
          aria-haspopup="dialog"
          className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-primary-500 text-white shadow-[0_10px_30px_rgba(32,70,180,0.35)] transition-all duration-200 hover:-translate-y-1 hover:bg-primary-600 hover:shadow-[0_14px_34px_rgba(32,70,180,0.42)] focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-200 md:h-16 md:w-16"
        >
          <i className="ri-question-line text-2xl md:text-[28px]" aria-hidden="true" />
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[150] flex items-center justify-center p-4 md:p-6"
            role="presentation"
          >
            <button
              type="button"
              className="absolute inset-0 cursor-default bg-foreground-950/60 backdrop-blur-sm"
              onClick={closeModal}
              aria-label="Cerrar instrucciones"
              tabIndex={-1}
            />

            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              aria-describedby={descriptionId}
              className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-2xl animate-[fadeIn_0.2s_ease-out]"
            >
              <header className="flex items-start gap-4 border-b border-background-200/80 px-5 py-5 md:px-7">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-customer-service-2-line text-2xl" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2
                    id={titleId}
                    className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl"
                  >
                    Cómo levantar un ticket
                  </h2>
                  <p
                    id={descriptionId}
                    className="mt-1 text-sm leading-relaxed text-foreground-500"
                  >
                    Sigue estos pasos para registrar y dar seguimiento a tu solicitud.
                  </p>
                </div>
                <button
                  ref={closeRef}
                  type="button"
                  onClick={closeModal}
                  aria-label="Cerrar"
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <i className="ri-close-line text-xl" aria-hidden="true" />
                </button>
              </header>

              <div className="overflow-y-auto px-5 py-5 md:px-7 md:py-6">
                <ol className="space-y-4">
                  {instructions.map((step, index) => (
                    <li key={step.title} className="flex gap-3.5 md:gap-4">
                      <div className="flex flex-col items-center">
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white">
                          {index + 1}
                        </span>
                        {index < instructions.length - 1 && (
                          <span className="mt-1 h-full w-px bg-primary-200" aria-hidden="true" />
                        )}
                      </div>
                      <div className="min-w-0 pb-2">
                        <div className="flex items-center gap-2">
                          <i
                            className={`${step.icon} text-base text-primary-500`}
                            aria-hidden="true"
                          />
                          <h3 className="text-sm font-semibold text-foreground-900 md:text-base">
                            {step.title}
                          </h3>
                        </div>
                        <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                          {step.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>

                <div className="mt-5 flex gap-3 rounded-xl border border-primary-100 bg-primary-50 p-4">
                  <i
                    className="ri-information-line mt-0.5 text-lg text-primary-600"
                    aria-hidden="true"
                  />
                  <p className="text-sm leading-relaxed text-primary-800">
                    Esta guía es solo informativa. Desde esta ventana no se crea ni se modifica
                    ningún ticket.
                  </p>
                </div>
              </div>

              <footer className="flex justify-end border-t border-background-200/80 bg-background-100/60 px-5 py-4 md:px-7">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 focus:outline-none focus:ring-4 focus:ring-primary-200"
                >
                  Entendido
                </button>
              </footer>
            </section>
          </div>,
          document.body
        )}
    </>
  );
}
